import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Autocomplete,
  Typography,
  Box,
  Alert,
  Divider,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LEAVE_TYPE_CHOICES, STATUS_CHOICES } from '../../utils/leaveConstants';
import FormEmployeeSearchField from '../common/FormEmployeeSearchField';
import FormSectionHeader from '../common/FormSectionHeader';

const LeaveForm = ({ open, mode, formData, formErrors, initialEmployee, onClose, onSubmit, onInputChange }) => {
  const getDialogTitle = () => {
    switch (mode) {
      case 'add':
        return 'Add New Leave Request';
      case 'edit':
        return 'Edit Leave Request';
      case 'view':
        return 'View Leave Request Details';
      default:
        return 'Leave Request';
    }
  };

  const isViewMode = mode === 'view';

  const getDialogSubtitle = () => {
    if (isViewMode) return 'Review leave dates, type, and approval status for this request.';
    if (mode === 'add') return 'Submit time off on behalf of an employee. End date must be on or after the start date.';
    return 'Update dates, type, or status. Changes apply to reports and attendance once saved.';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, display: 'block' }}>
          {getDialogTitle()}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 400, lineHeight: 1.65 }}>
          {getDialogSubtitle()}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 0.5 }}>
          {!isViewMode && (
            <Alert severity="info" variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              Approved leave may affect attendance. Set status to <strong>Pending</strong> until a manager reviews the
              request.
            </Alert>
          )}

          <FormSectionHeader
            title="Request details"
            description="Who is requesting leave, what type, and the current approval status."
          />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <FormEmployeeSearchField
                value={formData.employee}
                onChange={(id) => onInputChange('employee', id || '')}
                label="Employee"
                disabled={isViewMode}
                error={!!formErrors.employee}
                helperText={formErrors.employee || 'Search by name or employee ID.'}
                initialEmployee={initialEmployee}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Autocomplete
                value={LEAVE_TYPE_CHOICES.find((type) => type.value === formData.leave_type) || null}
                onChange={(event, newValue) => {
                  onInputChange('leave_type', newValue?.value || '');
                }}
                options={LEAVE_TYPE_CHOICES}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Leave type"
                    error={!!formErrors.leave_type}
                    helperText={formErrors.leave_type || 'Annual, sick, unpaid, or other policy category.'}
                  />
                )}
                disabled={isViewMode}
                clearOnEscape
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Autocomplete
                value={STATUS_CHOICES.find((status) => status.value === formData.status) || null}
                onChange={(event, newValue) => {
                  onInputChange('status', newValue?.value || '');
                }}
                options={STATUS_CHOICES}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Status"
                    error={!!formErrors.status}
                    helperText={formErrors.status || 'Pending, approved, or rejected.'}
                  />
                )}
                disabled={isViewMode}
                clearOnEscape
                fullWidth
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <FormSectionHeader
            title="Leave period"
            description="First and last day of absence. For a single day, use the same date for both."
          />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start date"
                  value={formData.start_date}
                  onChange={(date) => onInputChange('start_date', date)}
                  disabled={isViewMode}
                  sx={{ width: '100%' }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!formErrors.start_date,
                      helperText: formErrors.start_date || 'First day the employee is away.',
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="End date"
                  value={formData.end_date}
                  onChange={(date) => onInputChange('end_date', date)}
                  disabled={isViewMode}
                  sx={{ width: '100%' }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!formErrors.end_date,
                      helperText: formErrors.end_date || 'Last day of leave (inclusive).',
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <FormSectionHeader
            title="Reason"
            description="Brief explanation for approvers and HR records."
          />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason"
                multiline
                rows={4}
                value={formData.reason}
                onChange={(e) => onInputChange('reason', e.target.value)}
                error={!!formErrors.reason}
                helperText={formErrors.reason || 'Optional but recommended for audit and approval.'}
                disabled={isViewMode}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{isViewMode ? 'Close' : 'Cancel'}</Button>
        {!isViewMode && (
          <Button variant="contained" onClick={onSubmit}>
            {mode === 'edit' ? 'Update' : 'Create'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LeaveForm;
